import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Space,
  Typography,
  Button,
  Row,
  Col,
  Checkbox
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { CalendarEvent, EventType } from '@/types/community/calendar';
import { addCalendarEvent, updateCalendarEvent } from '@/api/calendar';
import { useAuth } from '@/contexts/AuthContext';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { VendorSelector } from '@/components/common/VendorSelector';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface CalendarEventModalProps {
  open: boolean;
  event?: CalendarEvent | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormValues {
  title: string;
  description: string;
  start: Dayjs;
  end: Dayjs;
  type: EventType;
  organizerType: 'employee' | 'resident' | 'vendor';
  employeeId?: string;
  residentId?: string;
  vendorId?: string;
  amenityId?: string;
  assetId?: string;
  notes?: string;
  isAllDay?: boolean;
  status?: 'scheduled' | 'cancelled' | 'completed';
}

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: 'meeting', label: 'Meeting' },
  { value: 'activity', label: 'Activity' },
  { value: 'other', label: 'Other' }
];

const ORGANIZER_TYPES = [
  { value: 'employee', label: 'Employee' },
  { value: 'resident', label: 'Resident' },
  { value: 'vendor', label: 'Vendor' }
];

const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  open,
  event,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation(['calendar', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const [organizerType, setOrganizerType] = useState<'employee' | 'resident' | 'vendor'>('employee');
  const [employeeData, setEmployeeData] = useState<{ id: string; name: string } | null>(null);
  const [residentData, setResidentData] = useState<{ id: string; name: string } | null>(null);
  const [vendorData, setVendorData] = useState<{ id: string; name: string } | null>(null);

  // Mutations
  const addEventMutation = useMutation({
    mutationFn: (data: Omit<CalendarEvent, 'id'>) => addCalendarEvent(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('calendar:eventCreated'));
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Add calendar event error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to add calendar event: ${errorMessage}`);
    }
  });

  const updateEventMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CalendarEvent> }) => 
      updateCalendarEvent(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('calendar:eventUpdated'));
      queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Update calendar event error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to update calendar event: ${errorMessage}`);
    }
  });

  useEffect(() => {
    if (open) {
      if (event) {
        // Determine organizer type from event data
        let orgType: 'employee' | 'resident' | 'vendor' = 'employee';
        if (event.hostedBy) {
          orgType = event.hostedBy.type;
        }

        form.setFieldsValue({
          title: event.title,
          description: event.description,
          start: dayjs(event.start),
          end: dayjs(event.end),
          type: event.type,
          organizerType: orgType,
          employeeId: event.hostedBy?.type === 'employee' ? event.hostedBy.id : undefined,
          residentId: event.hostedBy?.type === 'resident' ? event.hostedBy.id : undefined,
          vendorId: event.hostedBy?.type === 'vendor' ? event.hostedBy.id : undefined,
          notes: event.notes,
          isAllDay: event.isAllDay,
          status: event.status
        });
        setOrganizerType(orgType);
        setEmployeeData(event.hostedBy?.type === 'employee' ? event.hostedBy : null);
        setResidentData(event.hostedBy?.type === 'resident' ? event.hostedBy : null);
        setVendorData(event.hostedBy?.type === 'vendor' ? event.hostedBy : null);
      } else {
        form.resetFields();
        form.setFieldsValue({
          start: dayjs(),
          end: dayjs().add(1, 'hour'),
          type: 'meeting',
          organizerType: 'employee',
          status: 'scheduled'
        });
        setOrganizerType('employee');
        setEmployeeData(null);
        setResidentData(null);
        setVendorData(null);
      }
    }
  }, [open, event, form]);

  const handleSubmit = async (values: FormValues) => {
    try {
      if (!firestoreUser?.workspace) {
        throw new Error('No workspace available');
      }

      console.log('Form values:', values);
      
      // Additional client-side validation
      if (!values.title?.trim()) {
        message.error('Event title is required');
        return;
      }
      if (!values.description?.trim()) {
        message.error('Event description is required');
        return;
      }
      if (!values.type) {
        message.error('Event type is required');
        return;
      }
      if (!values.start || !dayjs.isDayjs(values.start)) {
        message.error('Event start time is required');
        return;
      }
      if (!values.end || !dayjs.isDayjs(values.end)) {
        message.error('Event end time is required');
        return;
      }
      if (values.start.isAfter(values.end)) {
        message.error('Start time cannot be after end time');
        return;
      }
      if (!values.organizerType) {
        message.error('Organizer type is required');
        return;
      }

      // Validate organizer data based on type
      if (values.organizerType === 'employee' && (!values.employeeId || !employeeData)) {
        message.error('Employee information is required');
        return;
      }
      if (values.organizerType === 'resident' && (!values.residentId || !residentData)) {
        message.error('Resident information is required');
        return;
      }
      if (values.organizerType === 'vendor' && (!values.vendorId || !vendorData)) {
        message.error('Vendor information is required');
        return;
      }

      const eventData: Omit<CalendarEvent, 'id'> = {
        title: values.title.trim(),
        description: values.description.trim(),
        start: values.start.toDate(),
        end: values.end.toDate(),
        type: values.type,
        hostedBy: values.organizerType === 'employee' ? (employeeData ? { ...employeeData, type: 'employee' } : undefined) :
                 values.organizerType === 'resident' ? (residentData ? { ...residentData, type: 'resident' } : undefined) :
                 values.organizerType === 'vendor' ? (vendorData ? { ...vendorData, type: 'vendor' } : undefined) : undefined,
        notes: values.notes || undefined,
        isAllDay: values.isAllDay || undefined,
        status: values.status || undefined,
        workspace: firestoreUser.workspace
      };

      console.log('Submitting calendar event data:', eventData);

      if (event) {
        if (event.id) {
          updateEventMutation.mutate({ id: event.id, data: eventData });
        } else {
          message.error('Cannot update event without ID');
        }
      } else {
        addEventMutation.mutate(eventData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleOrganizerTypeChange = (type: 'employee' | 'resident' | 'vendor') => {
    setOrganizerType(type);
    // Clear other organizer fields when type changes
    form.setFieldsValue({
      employeeId: undefined,
      residentId: undefined,
      vendorId: undefined
    });
    // Clear organizer data state
    setEmployeeData(null);
    setResidentData(null);
    setVendorData(null);
  };

  const renderOrganizerField = () => {
    switch (organizerType) {
      case 'employee':
        return (
          <Form.Item
            name="employeeId"
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <EmployeeSelector onEmployeeDataChange={setEmployeeData} />
          </Form.Item>
        );
      case 'resident':
        return (
          <Form.Item
            name="residentId"
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <ResidentSelector onResidentDataChange={setResidentData} />
          </Form.Item>
        );
      case 'vendor':
        return (
          <Form.Item
            name="vendorId"
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <VendorSelector onVendorDataChange={setVendorData} />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {event ? t('calendar:editEvent') : t('calendar:addEvent')}
        </Title>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          start: dayjs(),
          end: dayjs().add(1, 'hour'),
          type: 'meeting',
          organizerType: 'employee',
          status: 'scheduled'
        }}
      >
        <Form.Item
          name="title"
          label={t('calendar:title')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Input placeholder={t('calendar:eventTitlePlaceholder')} />
        </Form.Item>

        <Form.Item
          name="description"
          label={t('calendar:description')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea rows={3} placeholder={t('calendar:eventDescPlaceholder')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('calendar:type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('calendar:selectEventType')}>
                {EVENT_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('calendar:status')}
            >
              <Select>
                            <Select.Option value="scheduled">{t('calendar:statuses.scheduled')}</Select.Option>
            <Select.Option value="cancelled">{t('calendar:statuses.cancelled')}</Select.Option>
            <Select.Option value="completed">{t('calendar:statuses.completed')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="organizerType"
              label={t('calendar:organizerType')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select 
                placeholder={t('calendar:selectOrganizerType')}
                onChange={handleOrganizerTypeChange}
              >
                {ORGANIZER_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {renderOrganizerField()}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="start"
              label={t('calendar:startTime')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker 
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder={t('calendar:startTimePlaceholder')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="end"
              label={t('calendar:endTime')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker 
                showTime={{ format: 'HH:mm' }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: '100%' }}
                placeholder={t('calendar:endTimePlaceholder')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="isAllDay"
          valuePropName="checked"
        >
          <Checkbox>{t('calendar:allDay')}</Checkbox>
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('calendar:notes')}
        >
          <TextArea rows={2} placeholder={t('calendar:notesPlaceholder')} />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>{t('common:actions.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={addEventMutation.isPending || updateEventMutation.isPending}>
              {event ? t('common:actions.update') : t('common:actions.create')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CalendarEventModal; 